/**
 * Course-event tracking client. Posts batched events to
 * /api/v1/public/track on granguard-api.
 *
 * Design:
 *  - Fire-and-forget. Never blocks navigation.
 *  - Batched: events accumulated for ~1.5s then flushed, or flushed
 *    immediately on page-hide / 50-event cap.
 *  - Opt-out aware: short-circuits when sd_optout=1 cookie present.
 *  - Best-effort: a network error never throws — analytics must not
 *    break the user experience.
 *
 * Spec: docs/specs/sponsor-dashboard.md §7.1
 */
import { sdApi } from './api'
import { isOptedOut } from './optout'
import { resolveActiveTenantSlug } from './site'
import { COMMUNITY_PARTNER } from './community-partners'

export type ContentEventType =
    | 'CHAPTER_START'
    | 'CHAPTER_COMPLETE'
    | 'SLIDE_VIEW'
    | 'SLIDE_COMPLETE'
    | 'COURSE_COMPLETE'
    | 'FEEDBACK_SUBMITTED'

export interface ContentEvent {
    eventType: ContentEventType
    courseId?: string
    chapterId?: string
    slideId?: string
}

const FLUSH_DELAY_MS = 1500
const MAX_BATCH = 50

let queue: ContentEvent[] = []
let flushTimer: number | null = null

function flush() {
    if (flushTimer !== null) {
        window.clearTimeout(flushTimer)
        flushTimer = null
    }
    if (queue.length === 0) return
    const batch = queue
    queue = []
    // Attribute the batch to whichever tenant is active at flush time so the
    // partner's dashboard counts it. Null on the apex — recorded but unattributed.
    // Community partners (garvey.) are not tenant slugs; attribute to them too.
    const tenant = resolveActiveTenantSlug() ?? COMMUNITY_PARTNER?.slug ?? undefined
    sdApi.post('/public/track', { events: batch, tenant }).catch(() => {
        // swallow — tracking is best-effort
    })
}

function scheduleFlush() {
    if (flushTimer !== null) return
    flushTimer = window.setTimeout(flush, FLUSH_DELAY_MS)
}

export function trackEvent(event: ContentEvent): void {
    if (typeof window === 'undefined') return
    if (isOptedOut()) return
    queue.push(event)
    if (queue.length >= MAX_BATCH) {
        flush()
    } else {
        scheduleFlush()
    }
}

let listenerInstalled = false

/** Register page-hide / unload listeners that flush the queue. Idempotent. */
export function installTrackingFlushHandlers(): void {
    if (listenerInstalled) return
    if (typeof window === 'undefined') return
    listenerInstalled = true
    const flushOnHide = () => flush()
    window.addEventListener('pagehide', flushOnHide)
    window.addEventListener('beforeunload', flushOnHide)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush()
    })
}
